import styled, { css } from 'styled-components';

export const NODE_WIDTH = 220;
export const HANDLE_ROW_HEIGHT = 18;

/** Visual kinds drive the border + background variant. */
export type NodeKind =
  | 'initial'
  | 'output'
  | 'route'
  | 'forEach'
  | 'forEachBoundary'
  | 'normal'
  | 'unknown';

export const StyledNode = styled.div<{
  $kind: NodeKind;
  $selected: boolean;
  $invalid: boolean;
  $isForEach: boolean;
}>`
  padding: 6px;
  border-radius: ${({ theme }) => theme.radii.sm};
  border: ${({ $kind, theme }) => {
    switch ($kind) {
      case 'route':
        return `2px solid ${theme.colors.nodeRouteAccent}`;
      case 'forEach':
      case 'forEachBoundary':
        return `2px dashed ${theme.colors.nodeForEachBorder}`;
      case 'unknown':
        return `2px solid ${theme.colors.nodeUnknownBorder}`;
      default:
        return `1px solid ${theme.colors.borderDashed}`;
    }
  }};
  background: ${({ $kind, theme }) => {
    switch ($kind) {
      case 'route':
        return theme.colors.nodeRouteSurface;
      case 'forEach':
        return theme.colors.nodeForEachSurfaceFaded;
      case 'forEachBoundary':
        return theme.colors.nodeForEachSurface;
      case 'unknown':
        return theme.colors.nodeUnknownSurface;
      default:
        return theme.colors.panel;
    }
  }};
  width: ${({ $isForEach }) => ($isForEach ? '100%' : `${NODE_WIDTH}px`)};
  ${({ $isForEach }) => ($isForEach ? 'min-height: 100%;' : '')}
  box-sizing: border-box;
  position: relative;

  ${({ $selected, theme }) =>
    $selected &&
    css`
      box-shadow:
        0 0 0 2px ${theme.colors.primary},
        0 4px 12px ${theme.colors.nodeSelectedShadow};
    `};

  ${({ $invalid, $selected, theme }) =>
    $invalid &&
    css`
      border-color: ${theme.colors.dangerText};
      box-shadow: ${$selected
        ? `0 0 0 2px ${theme.colors.primary}, 0 0 0 4px ${theme.colors.nodeInvalidShadowSelected}`
        : `0 0 0 2px ${theme.colors.nodeInvalidShadow}`};
    `};
`;

export const StyledStepIdInput = styled.input`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radii.sm};
  padding: 1px 2px;
  outline: none;
  box-sizing: border-box;
`;

export const StyledStepIdLabel = styled.div`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: default;
`;

export const StyledContractName = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

export const StyledSubLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const StyledUnknownActionBanner = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textAccentWarn};
  font-weight: 600;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 3px;
`;

export const StyledOutputsBlock = styled.div`
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed ${({ theme }) => theme.colors.borderSubtle};
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const StyledOutputsHeading = styled.div`
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 600;
`;

export const StyledOutputRow = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  justify-content: space-between;
  gap: 4px;
`;

export const StyledOutputName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledOutputType = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-family: monospace;
  font-size: 9px;
`;

export const StyledDataflowRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

export const StyledDataflowColumn = styled.div<{ $align?: 'left' | 'right' }>`
  flex: 1;
  min-width: 0;
  ${({ $align }) => ($align === 'right' ? 'text-align: right;' : '')}
`;

export const StyledHandleRow = styled.div<{ $align?: 'left' | 'right' }>`
  position: relative;
  height: ${HANDLE_ROW_HEIGHT}px;
  display: flex;
  align-items: center;
  ${({ $align }) => ($align === 'right' ? 'justify-content: flex-end;' : '')}
`;

export const StyledHandleLabel = styled.span`
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
